import {
  AssemblyType,
  AssemblyTypeValue,
  InstallationStatus,
  JobFormDataBackflow,
  RpCheck2Type,
  TestResult,
  TestResultDc,
  TestResultRp,
  TestResultSvbPvb,
  TestResultType2Bypass,
} from '../../types/jobFormData';
import { toDecimal, toNumber } from '../number';
import {
  validateTestResultDc,
  validateTestResultRp,
  validateTestResultSvbPvb,
  validateTestResultType2Bypass,
} from './validate';
import {
  isTestable,
  rpCheck2Required,
  rpOpenedAtRequired,
  rpPressureDropRequired,
} from './helpers';

export type TestStatus = 'PASS' | 'FAIL' | 'REMOVED' | 'INCOMPLETE' | 'ERRORS';

export const getTestStatus = (
  testAttempt: TestResult,
  backflowAssemblyType: AssemblyTypeValue,
  rpCheck2Type: RpCheck2Type
): TestStatus => {
  switch (backflowAssemblyType) {
    case AssemblyType.RP:
    case AssemblyType.RPDA:
    case AssemblyType.RPDA_2:
      return getTestStatustRp(testAttempt as TestResultRp, rpCheck2Type);
    case AssemblyType.DC:
    case AssemblyType.DCDA:
    case AssemblyType.DCDA_2:
      return getTestStatustDc(testAttempt as TestResultDc);
    case AssemblyType.PVB:
    case AssemblyType.SVB:
      return getTestStatusSvbPvb(testAttempt as TestResultSvbPvb);
    case AssemblyType.AIR_GAP:
    case AssemblyType.AVB:
      return 'PASS';
    case AssemblyType.TYPE_2_BYPASS:
      return getTestStatusType2Bypass(testAttempt as TestResultType2Bypass);
    default:
      throw new Error(
        `Assembly type ${backflowAssemblyType} is not yet supported`
      );
  }
};

export const getFinalTestStatus = (
  testData: JobFormDataBackflow,
  rpCheck2Type: RpCheck2Type
): TestStatus => {
  if (testData.installationStatus === InstallationStatus.REMOVED) {
    return 'REMOVED';
  }

  if (
    !testData.backflowAssembly ||
    !testData.backflowAssembly.type ||
    !isTestable(testData)
  ) {
    return 'INCOMPLETE';
  }

  const tests = testData.tests;
  if (tests && tests.length > 0) {
    let result: TestStatus = 'INCOMPLETE';
    tests.forEach((testAttempt) => {
      if (testData.backflowAssembly?.type && testAttempt.type !== 'repair') {
        result = getTestStatus(
          testAttempt,
          testData.backflowAssembly.type,
          rpCheck2Type
        );
      }
    });
    return result;
  }

  return 'INCOMPLETE';
};

export const getTestStatustRp = (
  testAttempt: TestResultRp,
  rpCheck2Type: RpCheck2Type
): TestStatus => {
  const openedAt = toDecimal(testAttempt.openedAt);
  const pressureDrop = toDecimal(testAttempt.pressureDrop);
  const check2Other = toDecimal(testAttempt.check2Other);

  if (
    typeof testAttempt.systemPsi !== 'number' ||
    (rpOpenedAtRequired(testAttempt) && !openedAt) ||
    (rpPressureDropRequired(testAttempt) && !pressureDrop) ||
    (rpCheck2Required(testAttempt) &&
      (!testAttempt.check2 || (testAttempt.check2 === 'other' && !check2Other)))
  ) {
    return 'INCOMPLETE';
  }

  const errors = validateTestResultRp(testAttempt);
  if (errors.length > 0) {
    return 'ERRORS';
  }

  // when water supplier rpCheck2Type is USC10,        check2 can only be 'tight' or 'leaked'
  // when water supplier rpCheck2Type is VALUE,        check2 is automatically 'other', require check2Other and it has to be 1.0 or greater to pass
  // when water supplier rpCheck2Type is VALUE_BUFFER, check2 is automatically 'other', require check2Other and adjust test result status to include value buffer

  if (
    rpCheck2Type === 'VALUE_BUFFER' &&
    pressureDrop &&
    openedAt &&
    pressureDrop.minus(openedAt).lessThan(3)
  ) {
    return 'FAIL';
  }

  if (
    ['VALUE', 'VALUE_BUFFER'].includes(rpCheck2Type) &&
    (!testAttempt.check2 || testAttempt.check2 === 'other') &&
    check2Other?.lessThan(1)
  ) {
    return 'FAIL';
  }

  if (
    openedAt?.lessThan(2) ||
    testAttempt.didNotOpen ||
    testAttempt.check2 === 'leaked' ||
    pressureDrop?.lessThan(5) ||
    testAttempt.reliefValveDripping === 'upon_arrival' ||
    testAttempt.reliefValveDripping === 'upon_closure' ||
    (pressureDrop && openedAt && pressureDrop.minus(openedAt).lessThan(0.1))
  ) {
    return 'FAIL';
  }

  return 'PASS';
};

export const getTestStatustDc = (testAttempt: TestResultDc): TestStatus => {
  const systemPsi = toNumber(testAttempt.systemPsi);
  const check1 = toDecimal(testAttempt.check1);
  const check2 = toDecimal(testAttempt.check2);

  if (systemPsi === undefined || check1 === undefined || check2 === undefined) {
    return 'INCOMPLETE';
  }

  const errors = validateTestResultDc(testAttempt);
  if (errors.length > 0) {
    return 'ERRORS';
  }

  /*
   If either value < 1, show "Test Result" as Fail
  */
  return check1.lessThan(1) || check2.lessThan(1) ? 'FAIL' : 'PASS';
};

export const getTestStatusType2Bypass = (
  testAttempt: TestResultType2Bypass
): TestStatus => {
  const bypassCheck = toDecimal(testAttempt.bypassCheck);
  if (!bypassCheck) {
    return 'INCOMPLETE';
  }

  const errors = validateTestResultType2Bypass(testAttempt);
  if (errors.length > 0) {
    return 'ERRORS';
  }

  /*
   If either value < 1, show "Test Result" as Fail
  */
  return bypassCheck.lessThan(1) ? 'FAIL' : 'PASS';
};

export const getTestStatusSvbPvb = (
  testAttempt: TestResultSvbPvb
): TestStatus => {
  const openedAtRequired = !testAttempt.didNotOpen;
  const systemPsi = toNumber(testAttempt.systemPsi);
  const openedAt = toDecimal(testAttempt.openedAt);
  const pressureDrop = toDecimal(testAttempt.pressureDrop);
  if (
    systemPsi === undefined ||
    (openedAtRequired && openedAt === undefined) ||
    pressureDrop === undefined
  ) {
    return 'INCOMPLETE';
  }

  const errors = validateTestResultSvbPvb(testAttempt);
  if (errors.length > 0) {
    return 'ERRORS';
  }

  if (
    openedAt?.lessThan(1) ||
    testAttempt.didNotOpen ||
    pressureDrop?.lessThan(1)
  ) {
    return 'FAIL';
  }

  return 'PASS';
};
